import { useEffect, useState } from "react";
import { AutoComplete, Button, DatePicker, Form, Image, Input, Select, Space, Upload, message } from "antd";
import { FormRow, FormWrapper, OperationWrapper, Title, TitleWrapper, UpdateTenantInformationWrapper } from "./UpdateTenantInformation.styled";
import TenantApi from "../../../../../../../../api/TenantApi";
import dayjs from "dayjs";
import FileApi from "../../../../../../../../api/FileApi";
import { PlusOutlined } from "@ant-design/icons";
import Industry from "../../../../../../../../dict/Industry";
import Api from "../../../../../../../../api/Api";
import StringUtils from "../../../../../../../../util/StringUtils";

interface UpdateTenantInformationProps {
    tenantView: any,
    onClose: ()=> void,
    onComplete: ()=> void,
}

export const UpdateTenantInformation: React.FC<UpdateTenantInformationProps> = (props)=> {
    // api
    let tenantApi = new TenantApi();
    const [messageApi, contextHolder] = message.useMessage();

    // form
    const [form] = Form.useForm();

    // convert tenant view to tenant information
    let convert = (tenantView: any)=> {
        return {
            id: tenantView.tenant.id,
            name: tenantView.tenant.name,
            tenantNo: tenantView.tenant.tenantNo,
            avatar: tenantView.tenant.avatar,
            address: tenantView.tenant.address,
            industry: tenantView.tenant.industry,
            status: tenantView.tenant.status,
            expiredDate: tenantView.tenant.expireDate,
            remarks: tenantView.tenant.remarks,
        };
    };

    // state
    let [tenantInformation, setTenantInformation] = useState(convert(props.tenantView));
    let [submitEnabled, setSubmitEnabled] = useState(false);
    let [logoUploadFileList, setLogoUploadFileList] = useState<any[]>([
        {
            uid: '-1',
            name: 'logo.png',
            status: 'done',
            url: Api.URL_PREFIX + props.tenantView.tenant.avatar
        },
    ]);
    let [openPreview, setOpenPreview] = useState(false);

    // options
    let industries = Industry.RECORDS.map(e=> {
        return {
            id: e.ID,
            name: e.CODE,
        }
    });
    let industryOptions = industries.map((industry)=> {
        return {
            label: industry.name,
            value: industry.name,
        }
    });

    // options
    let statusOptions = [
        {label: '正常', value: 'ENABLED'},
        {label: '禁用', value: 'DISABLED'},
    ];

    // validate
    let validateForm = ()=> {
        form.validateFields().then((values)=> {
            setSubmitEnabled(true);
        }).catch((err)=> {
            console.log(err);
        });
    };

    // events
    let onNameChange = (e)=> {
        tenantInformation.name = e.target.value;
        setTenantInformation({...tenantInformation});
        validateForm();
    };

    let onAvatarChange = (e)=> {
        tenantInformation.avatar = e.target.value;
        setTenantInformation({...tenantInformation});
        validateForm();
    };

    let onAddressChange = (e)=> {
        tenantInformation.address = e.target.value;
        setTenantInformation({...tenantInformation});
        validateForm();
    };

    let onIndustryChange = (industry: string)=> {
        tenantInformation.industry = industry;
        setTenantInformation({...tenantInformation});
        validateForm();
    };

    let onStatusChange = (selectedValue)=> {
        tenantInformation.industry = selectedValue;
        setTenantInformation({...tenantInformation});
        validateForm();
    };

    let onExpiredDateChange = (dayjsValue)=> {
        tenantInformation.expiredDate = dayjs(dayjsValue).toDate().getTime();
        setTenantInformation({...tenantInformation});
        validateForm();
    };

    let onRemarksChange = (e)=> {
        tenantInformation.remarks = e.target.value;
        setTenantInformation({...tenantInformation});
        validateForm();
    };

    let onSubmit = async ()=> {
        await tenantApi.update({
            ...tenantInformation,
            positioningMethod: 'AUTO',
        });
        message.open({
            type: 'success',
            content: '更新租户成功',
        });
        props.onComplete();
        props.onClose();
    };

    let onCancel = ()=> {
        props.onClose();
    };

    let onPreview = (file)=> {
        setOpenPreview(true);
    };

    let onRemove = (file)=> {
        logoUploadFileList = [];
        setLogoUploadFileList([]);
        return true;
    };


    let onUpload = (file: any)=> {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
            let base64DataUrl = reader.result as string;
            tenantInformation.avatar = base64DataUrl;
            setTenantInformation({...tenantInformation});

            let logoUploadFile = {
                uid: file.uid,
                name: file.name,
                status: 'done',
                url: base64DataUrl,
            };
            logoUploadFileList = [logoUploadFile];
            setLogoUploadFileList(logoUploadFileList);
        }
        return false;
    };

    // effect
    let loadTenantView = async ()=> {
        let tenantView = (await tenantApi.getTenantViewById(props.tenantView.tenant.id)).data.data;
        let tenantInformation = convert(tenantView);
        setTenantInformation(tenantInformation);
        setLogoUploadFileList([
            {
                uid: '-1',
                name: 'logo.png',
                status: 'done',
                url: Api.URL_PREFIX + tenantView.tenant.avatar
              },
        ]);
        form.setFieldsValue(tenantInformation);
        validateForm();
    }

    useEffect(()=> {
        loadTenantView();
    }, []);

    // render
    return (
        <UpdateTenantInformationWrapper>
            {contextHolder}
            <TitleWrapper>
                <Title>编辑租户</Title>
            </TitleWrapper>

            <FormWrapper>
                <Form form={form} name="form" layout="vertical">
                    <FormRow>
                        <Form.Item name="avatar" label="LOGO" rules={[{required: true}]}>
                            <Upload
                                action={FileApi.UPLOAD_URL}
                                listType="picture-circle"
                                fileList={logoUploadFileList}
                                onPreview={onPreview}
                                onRemove={onRemove}
                                beforeUpload={onUpload}
                            >
                                {
                                    logoUploadFileList.length <=0 && 
                                    <button style={{ border: 0, background: 'none' }} type="button">
                                        <PlusOutlined />
                                        <div style={{ marginTop: 8 }}>Upload</div>
                                    </button>
                                }
                            </Upload>

                            <Image
                                wrapperStyle={{ display: 'none' }}
                                src={logoUploadFileList[0]?.url}
                                preview={{
                                    visible: openPreview,
                                    onVisibleChange: (visible) => setOpenPreview(visible),
                                    afterOpenChange: (visible) => !visible && setOpenPreview(false),
                                }}
                            />
                        </Form.Item>
                    </FormRow>

                    <FormRow>
                        <Form.Item name="name" label="租户名称" rules={[{required: true}]} initialValue={tenantInformation.name}>
                            <Input onChange={onNameChange} />
                        </Form.Item>

                        <Form.Item name="tenantNo" label="租户编号" rules={[{required: true}]} initialValue={tenantInformation.tenantNo}>
                            <Input disabled/>
                        </Form.Item>
                    </FormRow>

                    <FormRow>
                        <Form.Item name="address" label="地址" rules={[{required: true}]} initialValue={tenantInformation.address}>
                            <Input onChange={onAddressChange} />
                        </Form.Item>

                        <Form.Item name="industry" label="行业" rules={[{required: true}]} initialValue={tenantInformation.industry}>
                            <AutoComplete
                                options={industryOptions}
                                placeholder="请输入行业"
                                filterOption={(inputValue, option) =>
                                    StringUtils.nonull(option.value).toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                                    || StringUtils.nonull(option.label).toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                                }
                                onChange={onIndustryChange}
                            />
                        </Form.Item>
                    </FormRow>

                    <FormRow>
                        <Form.Item name="expiredDate" label="过期时间" rules={[{required: false}]} initialValue={tenantInformation.expiredDate}>
                            <DatePicker
                                format="YYYY-MM-DD"
                                defaultValue={dayjs().add(365, 'd')}
                                value={dayjs(tenantInformation.expiredDate)}
                                onChange={onExpiredDateChange}
                            />
                        </Form.Item>

                        <Form.Item name="status" label="状态" rules={[{required: true}]} initialValue={tenantInformation.status}>
                            <Select
                                allowClear
                                placeholder="请选择状态"
                                defaultValue={'ACTIVE'}
                                value={tenantInformation.status}
                                onChange={onStatusChange}
                                options={statusOptions}
                            />
                        </Form.Item>
                    </FormRow>

                    <FormRow>
                        <Form.Item name="remarks" label="备注" rules={[{required: false}]} initialValue={tenantInformation.remarks}>
                            <Input.TextArea style={{height: '80px'}} onChange={onRemarksChange}/>
                        </Form.Item>
                    </FormRow>
                </Form>
            </FormWrapper>

            <OperationWrapper>
                <Space size="middle">
                    <Button type="primary" disabled={!submitEnabled} onClick={onSubmit}>确定</Button>
                    <Button type="primary" danger onClick={onCancel}>取消</Button>
                </Space>
            </OperationWrapper>
        </UpdateTenantInformationWrapper>
    );
};

export default UpdateTenantInformation;
